import React, {useCallback, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import 'ui/FFButton/style.scss';
import {ChevronDownIcon} from "icons/svg";
import {Preloader} from "ui/Preloader";
import {FFDropdown} from "ui/FFDropdown";
import cn from "classnames";
import {MLine, usePropsBasedState} from "utils";
import {useFloatingPanel} from "hooks/useFloatingPanel";

export type FFButtonProps = Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "ref" | "children" | "type" | "onClick"
> & {
    ref?: React.Ref<HTMLButtonElement>;
    children?: React.ReactNode;
    secondary?: boolean;
    center?: boolean;
    warning?: boolean;
    type?: "button" | "submit" | "reset";
    href?: string;
    /**
     * `s` / `m` / `l` are the Figma `.base/Button` sizes (32 / 40 / 48px). `small` and
     * `mobile-small` predate them and stay for the screens that still use them.
     */
    size?: "small" | "mobile-small" | "s" | "m" | "l";
    /** `white` is the outlined pill on a white ground (Log in or Sign up, Get $7); `ghost` has no box
     *  at all; `gradient` is the accent gradient of the Humanizer's offer (Figma `result_button`). */
    variant?: "primary" | "secondary" | "white" | "ghost" | "gradient";
    /** Leading icon, sized by the button (16px at `s`, 20px otherwise). */
    icon?: React.ReactNode;
    /** Replaces the icon slot with the spinner and greys the button out. */
    loading?: boolean;
    /** Stretch to the container's width. */
    block?: boolean;
    onClick?: (e: React.MouseEvent) => void;
};

// Browser translation swaps a bare text node for <font> wrappers, so React's later
// insertBefore(spinner, textNode) on a loading flip throws NotFoundError and the route
// error page takes the screen. A text label inside an element survives the swap.
// Each run of adjacent text goes into one span, so it stays one flex item as before.
const translationSafe = (children: React.ReactNode) => {
    const isText = (c: unknown) => typeof c === 'string' || typeof c === 'number';
    const parts = React.Children.toArray(children);
    if (!parts.some(isText)) return children;
    const out: React.ReactNode[] = [];
    let run: React.ReactNode[] = [];
    const flush = () => {
        if (run.length) out.push(<span key={`text-${out.length}`}>{run}</span>);
        run = [];
    };
    parts.forEach((c) => {
        if (isText(c)) return run.push(c);
        flush();
        out.push(c);
    });
    flush();
    return out;
};

export const FFButton = ({ref, type="button", children, secondary = false, className, center = true, size, variant, icon, loading, block, disabled, ...rest}: FFButtonProps) => {
    const cls_ = cn('bf-button', {'center': center}, (secondary || variant === 'secondary') && 'bf-button--secondary',
        {"bf-button--warning": rest.warning},
        variant === 'white' && 'bf-button--white',
        variant === 'ghost' && 'bf-button--ghost',
        variant === 'gradient' && 'bf-button--gradient',
        size === 'small' && 'bf-button--small',
        size === 'mobile-small' && 'bf-button--mobile-small',
        size === 's' && 'bf-button--s',
        size === 'm' && 'bf-button--m',
        size === 'l' && 'bf-button--l',
        loading && 'bf-button--loading',
        block && 'bf-button--block',
        className);
    const leading = loading ? <Preloader/> : icon;

    return (
        <button ref={ref} className={cls_} type={type} disabled={disabled || loading} {...rest}>
            {leading && <span className="bf-button__icon">{leading}</span>}
            {translationSafe(children)}
        </button>
    );
}

export const Button = FFButton;

type SecondaryButtonProps = Omit<FFButtonProps, "secondary">;

export function SecondaryButton(props: SecondaryButtonProps) {
    return <FFButton secondary {...props} />;
}

// The menu is portaled into <body> and placed in viewport coordinates: inside the
// trigger, an ancestor with `overflow` would clip it (the mobile scan page's scroller
// starts right under the header) and one that is the containing block of a fixed box
// (a transform, a `container-type`) would shift it — or push it off the screen. It
// opens on the `location` side when that fits and the other one when it does not,
// lined up with the chevron's edge, and closes on scroll.
/**
 * A button with a menu behind its chevron. The icon (or the spinner while loading)
 * lives in the label section with the text, so the two stay centred together and
 * the chevron section is the only thing off to the end.
 */
export const FFDropdownButton = ({open=false, children, dropdown, sepArrow, onClick, location = 'bottom', className, ref, icon, loading, disabled, ...props}: {
    children: React.ReactNode,
    dropdown: React.ReactNode,
    sepArrow?: boolean,
    location?: 'bottom' | 'top',
    open?: boolean;
} & FFButtonProps) => {
    const [optionsOpen, setOptionsOpen] = usePropsBasedState(open);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const close = useCallback(() => setOptionsOpen(false), [setOptionsOpen]);
    const {style: anchor, side} = useFloatingPanel(optionsOpen, triggerRef, menuRef, {side: location, align: 'end', onHide: close});

    // Tippy (OnboardingTooltip) wraps this button and hands down its own callback
    // ref to use as the tooltip's reference element — ours has to ride along
    // rather than replace it.
    const setTrigger = useCallback((node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.RefObject<HTMLButtonElement | null>).current = node;
    }, [ref]);

    useEffect(() => {
        const hide = (e: MouseEvent) => {
            setOptionsOpen(false);
        }
        document.addEventListener("click", hide)
        return () => document.removeEventListener("click", hide)
    }, []);

    // React events bubble through the portal: a pick must not reach the button's own
    // action — and it is a pick, so the menu goes with it.
    const menu = optionsOpen ? createPortal(
        <FFDropdown
            ref={menuRef}
            top={side === 'top'}
            className="bf-button-dropdown-menu ff-dropdown--floating"
            style={anchor}
            onClick={e => { e.stopPropagation(); setOptionsOpen(false); }}
        >
            {dropdown}
        </FFDropdown>,
        document.body,
    ) : null;

    const leading = loading ? <Preloader/> : icon;
    return (
        <FFButton
            ref={setTrigger}
            className={cn('bf-button-with-dropdown', {
                "active": optionsOpen,
                "bf-button-with-dropdown--sep-arrow": sepArrow,
                "bf-button--loading": loading,
            }, className)}
            aria-expanded={optionsOpen}
            center={false}
            disabled={disabled || loading}
            onClick={(e) => {
                if (!onClick) {
                    setOptionsOpen((prev: any) => !prev);
                    e?.stopPropagation();
                } else {
                    setOptionsOpen(false);
                    onClick(e);
                }
            }}
            {...props}
        >
            {menu}

            <div className="ff-button-children-wrapper">
                {leading && <span className="bf-button__icon">{leading}</span>}
                {translationSafe(children)}
            </div>
            <div
                className={cn({"bf-button-arrow-right": sepArrow})}
                onClick={(e) => {
                    e.stopPropagation();
                    setOptionsOpen((prev: any) => !prev);
                }}
            >
                    {/* Points down at the menu it opens; open, it turns back up. */}
                    <MLine center
                        className={cn('scan-options-dropdown-chevron ', {'scan-options-chevron-active': optionsOpen})}
                    >
                            <ChevronDownIcon/>
                    </MLine>
            </div>

        </FFButton>
    )
}
